import { ChangeEvent, FC, useEffect } from "react";
import styles from "./CatalogFilter.module.scss";
import { CatalogQuery, CatalogQueryParam } from "@/lib/types/catalogPage";
import { Checkbox } from "@/lib/ui/components/Input";
import { useCatalogFilter } from "@/lib/hooks/useCatalogFilter";
import { useFieldArray, useForm } from "react-hook-form";
import cl from "classnames";
import { LCPhuketDistrict, PhuketDistrict } from "@/lib/types/complexPage";
import { IValueLabel } from "@/lib/types/mainPage";

interface ICatalogCheckboxBlock {
  items: IValueLabel[];
  catalogQuery: CatalogQueryParam;
  isTwoColumns?: boolean;
  isButton?: boolean;
  isHidden?: boolean;
  selectAllCheckbox?: {
    label: string;
  };
  className?: string;
  onMouseEnter?: (id: string) => void;
  onMouseLeave?: (id: string) => void;
}

const selectSubRegions = (
  id: string,
  values: LCPhuketDistrict[],
  items: IValueLabel[],
  remove?: boolean,
) => {
  const disabledState: Partial<Record<LCPhuketDistrict, boolean>> = items?.reduce(
    (a: Partial<Record<LCPhuketDistrict, boolean>>, b) => {
      a[b.value as LCPhuketDistrict] = !!b.isDisabled;
      return a;
    },
    {},
  );
  if (id === "bang-tao") {
    if (remove) {
      return values;
    } else {
      (["lagoon", "layan"] as LCPhuketDistrict[]).forEach((val: LCPhuketDistrict) => {
        if (!disabledState[val]) {
          values.push(val);
        }
      });
    }
  } else if (id === "karon") {
    if (remove) {
      return values;
    } else {
      (["kata-noi", "kata"] as LCPhuketDistrict[]).forEach((val: LCPhuketDistrict) => {
        if (!disabledState[val]) {
          values.push(val);
        }
      });
    }
  } else if (id === "rawai") {
    if (remove) {
      return values;
    } else {
      if (!disabledState["nai-harn"]) {
        values.push("nai-harn");
      }
    }
  }
  return Array.from(new Set(values));
};

export const CatalogCheckboxBlock: FC<ICatalogCheckboxBlock> = ({
  catalogQuery,
  items,
  isTwoColumns,
  isButton,
  isHidden,
  selectAllCheckbox,
  className,
  onMouseLeave,
  onMouseEnter,
}) => {
  const { setParam, query } = useCatalogFilter();
  const { register, watch, getValues, setValue, control } = useForm({
    mode: "onChange",
  });

  const { replace } = useFieldArray({ control, name: catalogQuery });

  useEffect(() => {
    const newValue = Array.isArray(query[catalogQuery])
      ? Array.from(new Set(query[catalogQuery] as any[]))
      : query[catalogQuery]
        ? [query[catalogQuery]]
        : undefined;
    setValue(catalogQuery, newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query[catalogQuery]]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    let values: string[] = watch()[catalogQuery];
    if (catalogQuery === "phuketDistrict") {
      values = selectSubRegions(
        e.target.value,
        values as LCPhuketDistrict[],
        items,
        !e.target.checked,
      );
    }

    setParam({ key: catalogQuery, value: values.filter((val) => val !== "all") });
  };

  const handler = register(catalogQuery);
  const values = getValues(catalogQuery);

  const getIsCheckedState = (value: string) => {
    const res = Array.isArray(values)
      ? values?.includes(value)
      : typeof values === "string"
        ? values === value
        : false;
    if (query[catalogQuery] === undefined) {
      return false;
    }
    return res;
  };

  return (
    <form
      className={cl(
        styles.form,
        isTwoColumns && styles["form--columns"],
        isHidden && styles["form--hidden"],
        className,
      )}
    >
      {selectAllCheckbox && (
        <Checkbox
          label={selectAllCheckbox.label}
          value={"all"}
          className={styles.form__all}
          isButton={isButton}
          {...handler}
          theme="dark"
          onChange={(e) => {
            replace("all");
            handler.onChange(e);
            if (query[catalogQuery] === "all") {
              setParam({ key: catalogQuery, value: undefined });
            } else {
              setParam({ key: catalogQuery, value: "all" });
            }
          }}
          name="all"
          isChecked={getIsCheckedState("all")}
          isPaleUncheckedLabel
          readOnly={false}
        />
      )}

      {items.map((item) => {
        const isChecked =
          getIsCheckedState(String(item.value)) ||
          (!!(selectAllCheckbox && query[catalogQuery] === item.value) && !item.isDisabled);
        return (
          <Checkbox
            theme="dark"
            key={item.label + item.value}
            label={item.label[0].toUpperCase() + item.label.slice(1)}
            value={item.value}
            isButton={isButton}
            id={item.id}
            {...handler}
            onChange={(e) => {
              handler.onChange(e);
              onChange(e);
            }}
            isChecked={isChecked}
            isPaleUncheckedLabel
            isDisabled={item.isDisabled && !isChecked}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            readOnly={false}
          />
        );
      })}
    </form>
  );
};
